import {
  ResumeSectionType,
  ContactInfo,
  WorkExperienceCompany,
  Education,
  SkillCategory,
  Certification,
  Project,
  ProfessionalSummaryEntry,
  contactDetailLineParts,
  contactFullName,
} from '@applicator/shared'
import React from 'react'

import ContactDetailLine from '../ContactDetailLine'
import WorkExperienceRoleTealHeader from '../WorkExperienceRoleTealHeader'
import { getSelectedTargetTitle } from '../../../utils/targetTitleUtils'
import {
  isWorkExperienceCompanyDescriptionInPreview,
  isWorkExperienceCompanyInPreview,
  isWorkExperienceRoleInPreview,
} from '../../../utils/workExperiencePreview'
import type { ResolvedResumeColors } from '../../../types/resumeColorScheme'
import { PDF_CONFIG } from '../../../utils/resumeExportPdfConfig'
import { PROJECTS_SECTION_IN_PREVIEW } from '../../../utils/resumePreviewFlags'
import {
  formatCertificationDates,
  isCertificationCredentialLinkInPreview,
  isCertificationDatesInPreview,
  isCertificationInPreview,
  isCertificationIssuerInPreview,
  isCertificationNameInPreview,
} from '../../../utils/certificationPreview'
import {
  TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM,
  TWO_COLUMN_SECTION_LINE_TO_BODY_GAP_MM,
  TWO_COLUMN_SECTION_RULE_THICKNESS_MM,
  TWO_COLUMN_SECTION_TITLE_TO_LINE_GAP_MM,
  twoColumnColgroupPercents,
} from '../../../utils/resumeExportPdfConfig'
import {
  mmToPx,
  measureMmToPx,
  twoColumnPreviewDividerToContentGapPx,
} from '../../../utils/resumePreviewPageLayout'
import type { ResumePreviewColumnSlice } from '../ResumePreviewColumnSliceContext'

const TEXT_BLACK_CLASS = 'text-black'

/** Teal proportions: left / gap / right as % of printable content width (scale with preview). */
const TWO_COL_PCT = twoColumnColgroupPercents()

interface WorkExperienceRole {
  id: string
  position?: string
  positionIncluded?: boolean
  employmentType?: string
  employmentTypeIncluded?: boolean
  location?: string
  locationIncluded?: boolean
  startDate?: string
  endDate?: string
  bulletPoints?: Array<{ id: string; text: string; included: boolean }>
  included?: boolean
}

/** Same section header styling as classic ResumePreview */
function SectionHeader({ title, accentHex }: { title: string; accentHex: string }) {
  const mmRatio = measureMmToPx()
  const titleToLinePx = mmToPx(TWO_COLUMN_SECTION_TITLE_TO_LINE_GAP_MM, mmRatio)
  const rulePx = mmToPx(TWO_COLUMN_SECTION_RULE_THICKNESS_MM, mmRatio)
  const lineToBodyPx = mmToPx(TWO_COLUMN_SECTION_LINE_TO_BODY_GAP_MM, mmRatio)
  return (
    <div style={{ marginBottom: lineToBodyPx }}>
      <h2
        className="resume-section-header text-sm font-normal uppercase"
        style={{ color: accentHex, marginBottom: titleToLinePx }}
      >
        {title}
      </h2>
      <div className="border-b border-black" style={{ height: rulePx, marginBottom: 0 }} />
    </div>
  )
}

export type PaysafePreviewHandlers = {
  onWorkExperienceRoleClick?: (roleId: string) => void
  onWorkExperienceBulletClick?: (roleId: string, bulletId: string) => void
  onTargetTitleItemClick?: (title: string) => void
  onProfessionalSummaryClick?: (summaryId: string) => void
  onEducationClick?: (educationId: string) => void
  onSkillCategoryClick?: (categoryId: string) => void
  onCertificationClick?: (certId: string) => void
  onProjectClick?: (projectId: string) => void
}

export default function PaysafeTwoColumnPreview({
  data,
  colors,
  columnSlice = 'full',
  onWorkExperienceRoleClick,
  onWorkExperienceBulletClick,
  onTargetTitleItemClick,
  onProfessionalSummaryClick,
  onEducationClick,
  onSkillCategoryClick,
  onCertificationClick,
  onProjectClick,
}: {
  data: Record<ResumeSectionType, Record<string, unknown>>
  colors: ResolvedResumeColors
  columnSlice?: ResumePreviewColumnSlice
} & PaysafePreviewHandlers) {
  const contactInfo = data.contact_info as ContactInfo
  const fullName = contactFullName(contactInfo) || 'Your Name'
  const contactDetailParts = contactDetailLineParts(contactInfo)

  const workCompanies = (data.work_experience as WorkExperienceCompany[] | undefined)?.filter(
    (c) => isWorkExperienceCompanyInPreview(c)
  )
  const educations = (data.education as Education[] | undefined)?.filter((e) => e.included ?? false)
  const skillCategories = data.skills as SkillCategory[] | undefined
  const hasSkills =
    skillCategories?.some((cat) => cat.skills.some((s) => s.included)) ?? false
  const certifications = (data.certifications as Certification[] | undefined)?.filter((c) =>
    isCertificationInPreview(c)
  )
  const projects = (data.projects as Project[] | undefined)?.filter((p) => p.included)
  const interests = (data.interests as Array<{ name: string; included?: boolean }> | undefined)?.filter(
    (i) => i.included !== false
  )

  const selectedTargetTitle = getSelectedTargetTitle(data.target_title)
  const hasTargetTitles = Boolean(selectedTargetTitle)
  const hasSummary =
    data.professional_summary &&
    Array.isArray(data.professional_summary) &&
    data.professional_summary.some((e: ProfessionalSummaryEntry) => e.included)

  /** Same px gap as PDF `TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM` (target→summary and SKILLS rule→first category). */
  const columnBodyTopGapPx = mmToPx(TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM, measureMmToPx())
  /** Divider → first column line; lives inside column cells so paged slices keep the gap when preview is fit-scaled. */
  const dividerToContentGapPx = twoColumnPreviewDividerToContentGapPx(measureMmToPx())
  const columnTopPadStyle = { paddingTop: dividerToContentGapPx } as const

  const leftColumnContent = (
    <>
              {hasTargetTitles && selectedTargetTitle && (
                <div style={{ marginBottom: columnBodyTopGapPx }}>
                  <h2
                    role="button"
                    tabIndex={0}
                    onClick={() => onTargetTitleItemClick?.(selectedTargetTitle)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && onTargetTitleItemClick) {
                        e.preventDefault()
                        onTargetTitleItemClick(selectedTargetTitle)
                      }
                    }}
                    className={`text-base font-semibold ${TEXT_BLACK_CLASS} mb-2 cursor-pointer hover:opacity-80 transition-opacity`}
                  >
                    {selectedTargetTitle}
                  </h2>
                </div>
              )}

              {hasSummary && (
                <div className="mb-6">
                  {data.professional_summary
                    .filter((entry: ProfessionalSummaryEntry) => entry.included)
                    .map((entry: ProfessionalSummaryEntry, idx: number) => (
                      <p
                        key={entry.id || idx}
                        role="button"
                        tabIndex={0}
                        className={`text-sm font-normal ${TEXT_BLACK_CLASS} leading-relaxed mb-3 whitespace-pre-wrap cursor-pointer hover:opacity-80 transition-opacity`}
                        onClick={() => onProfessionalSummaryClick?.(entry.id)}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && onProfessionalSummaryClick) {
                            e.preventDefault()
                            onProfessionalSummaryClick(entry.id)
                          }
                        }}
                      >
                        {entry.text}
                      </p>
                    ))}
                </div>
              )}

              {workCompanies && workCompanies.length > 0 && (
                <div className="mb-6">
                  <SectionHeader title="WORK EXPERIENCE" accentHex={colors.sectionHeaderHex} />
                  {workCompanies.map((company, companyIndex) => (
                    <div key={`${company.id}-${companyIndex}`} className="mb-4">
                      {(company.roles || [])
                        .filter((role: WorkExperienceRole) =>
                          isWorkExperienceRoleInPreview(company, role as import('@applicator/shared').WorkExperienceRole)
                        )
                        .map((role: WorkExperienceRole, roleIndex: number) => (
                          <div
                            key={`${role.id}-${roleIndex}`}
                            role="button"
                            tabIndex={0}
                            className="mb-4 cursor-pointer hover:opacity-80"
                            onClick={() => onWorkExperienceRoleClick?.(role.id)}
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && onWorkExperienceRoleClick) {
                                e.preventDefault()
                                onWorkExperienceRoleClick(role.id)
                              }
                            }}
                          >
                            <WorkExperienceRoleTealHeader
                              company={company}
                              role={role as import('@applicator/shared').WorkExperienceRole}
                              textClassName={TEXT_BLACK_CLASS}
                              lines="company"
                            />
                            {isWorkExperienceCompanyDescriptionInPreview(
                              company,
                              role as import('@applicator/shared').WorkExperienceRole
                            ) && (
                              <p
                                className={`font-normal mb-2 whitespace-pre-wrap ${TEXT_BLACK_CLASS}`}
                                style={{
                                  fontSize: `${PDF_CONFIG.fontSize.body}pt`,
                                  lineHeight: PDF_CONFIG.lineHeight.body,
                                }}
                              >
                                {company.description}
                              </p>
                            )}
                            <WorkExperienceRoleTealHeader
                              company={company}
                              role={role as import('@applicator/shared').WorkExperienceRole}
                              textClassName={TEXT_BLACK_CLASS}
                              lines="position"
                            />
                            {(role.bulletPoints || []).filter((bp) => bp.included).length > 0 && (
                              <ul className="text-sm font-normal text-black space-y-1 ml-8 list-none">
                                {(role.bulletPoints || [])
                                  .filter((bp) => bp.included)
                                  .map((bp, bpIndex) => (
                                    <li
                                      key={`${bp.id}-${bpIndex}`}
                                      role="button"
                                      tabIndex={0}
                                      className="paysafe-preview-bullet flex items-start cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        onWorkExperienceBulletClick?.(role.id, bp.id)
                                      }}
                                      onKeyDown={(e) => {
                                        if ((e.key === 'Enter' || e.key === ' ') && onWorkExperienceBulletClick) {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          onWorkExperienceBulletClick(role.id, bp.id)
                                        }
                                      }}
                                    >
                                      <span className="mr-2 shrink-0">•</span>
                                      <span className="paysafe-preview-bullet-text flex-1 leading-relaxed whitespace-pre-wrap">
                                        {bp.text}
                                      </span>
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              )}

              {PROJECTS_SECTION_IN_PREVIEW && projects && projects.length > 0 && (
                <div className="mb-6">
                  <SectionHeader title="PROJECTS" accentHex={colors.sectionHeaderHex} />
                  {projects.map((project, idx) => (
                    <div
                      key={project.id || idx}
                      role="button"
                      tabIndex={0}
                      className="mb-4 cursor-pointer hover:opacity-80"
                      onClick={() => project.id && onProjectClick?.(project.id)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && project.id && onProjectClick) {
                          e.preventDefault()
                          onProjectClick(project.id)
                        }
                      }}
                    >
                      <div className="flex justify-between mb-1">
                        <h3 className={`text-sm font-bold ${TEXT_BLACK_CLASS}`}>{project.name}</h3>
                        {project.startDate && project.endDate && (
                          <span className={`text-sm font-normal ${TEXT_BLACK_CLASS} whitespace-nowrap ml-2`}>
                            {project.startDate} - {project.endDate}
                          </span>
                        )}
                      </div>
                      {project.company && (
                        <p className={`text-sm font-normal ${TEXT_BLACK_CLASS} mb-2`}>{project.company}</p>
                      )}
                      {project.bulletPoints?.filter((bp) => bp.included).length ? (
                        <ul className={`text-sm font-normal ${TEXT_BLACK_CLASS} space-y-1 ml-8 list-none`}>
                          {project.bulletPoints
                            .filter((bp) => bp.included)
                            .map((bp, bpIdx) => (
                              <li key={bpIdx} className="paysafe-preview-bullet flex items-start">
                                <span className="mr-2 shrink-0">•</span>
                                <span className="paysafe-preview-bullet-text flex-1 whitespace-pre-wrap">
                                  {bp.text}
                                </span>
                              </li>
                            ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
    </>
  )

  const rightColumnContent = (
    <>
              {hasSkills && skillCategories && (
                <div className="mb-6">
                  <SectionHeader title="SKILLS" accentHex={colors.sectionHeaderHex} />
                  {skillCategories.map((category) => {
                    const included = category.skills.filter(
                      (s) => s.included && String(s.name ?? '').trim().length > 0
                    )
                    if (included.length === 0) return null
                    return (
                      <div
                        key={category.id}
                        role="button"
                        tabIndex={0}
                        className="mb-3 paysafe-skill-block cursor-pointer hover:opacity-80"
                        onClick={() => category.id && onSkillCategoryClick?.(category.id)}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && category.id && onSkillCategoryClick) {
                            e.preventDefault()
                            onSkillCategoryClick(category.id)
                          }
                        }}
                      >
                        <h3 className={`text-sm font-bold ${TEXT_BLACK_CLASS} mb-1`}>{category.name}:</h3>
                        <div className={`paysafe-skill-body text-sm font-normal ${TEXT_BLACK_CLASS}`}>
                          {included.map((s, skillIdx) => (
                            <span key={s.id ?? skillIdx} className="paysafe-skill-token">
                              {s.name}
                              {skillIdx < included.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {educations && educations.length > 0 && (
                <div className="mb-6">
                  <SectionHeader title="EDUCATION" accentHex={colors.sectionHeaderHex} />
                  {educations.map((edu, idx) => (
                    <div
                      key={edu.id || idx}
                      role="button"
                      tabIndex={0}
                      className="mb-3 paysafe-right-block cursor-pointer hover:opacity-80"
                      onClick={() => edu.id && onEducationClick?.(edu.id)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && edu.id && onEducationClick) {
                          e.preventDefault()
                          onEducationClick(edu.id)
                        }
                      }}
                    >
                      {(edu.degreeIncluded ?? false) && edu.degree && (
                        <div className={`text-sm font-bold ${TEXT_BLACK_CLASS} leading-snug`}>
                          {edu.degree}
                          {edu.fieldOfStudy && (
                            <span className="font-bold"> degree in {edu.fieldOfStudy}</span>
                          )}
                        </div>
                      )}
                      {edu.institution && (
                        <div className={`text-sm font-bold ${TEXT_BLACK_CLASS} mt-0.5`}>
                          {edu.institution}
                        </div>
                      )}
                      {(((edu.startDateIncluded ?? false) && edu.startDate) ||
                        ((edu.endDateIncluded ?? false) && edu.endDate)) && (
                        <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} mt-0.5`}>
                          {(edu.startDateIncluded ?? false) && edu.startDate ? edu.startDate : ''}
                          {(edu.startDateIncluded ?? false) &&
                          edu.startDate &&
                          (edu.endDateIncluded ?? false) &&
                          edu.endDate
                            ? ' - '
                            : ''}
                          {(edu.endDateIncluded ?? false) && edu.endDate ? edu.endDate : ''}
                        </div>
                      )}
                      {(edu.additionalInformationIncluded ?? false) && edu.additionalInformation && (
                        <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} mb-2 mt-1 leading-relaxed whitespace-pre-wrap`}>
                          {edu.additionalInformation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {certifications && certifications.length > 0 && (
                <div className="mb-6">
                  <SectionHeader title="CERTIFICATIONS" accentHex={colors.sectionHeaderHex} />
                  {certifications.map((cert, idx) => (
                    <div
                      key={cert.id || idx}
                      role="button"
                      tabIndex={0}
                      className="mb-3 paysafe-right-block cursor-pointer hover:opacity-80"
                      onClick={() => cert.id && onCertificationClick?.(cert.id)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && cert.id && onCertificationClick) {
                          e.preventDefault()
                          onCertificationClick(cert.id)
                        }
                      }}
                    >
                      {isCertificationNameInPreview(cert) &&
                        (isCertificationCredentialLinkInPreview(cert) ? (
                          <a
                            href={cert.credentialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={`text-sm font-bold ${TEXT_BLACK_CLASS} hover:text-interactive underline leading-snug`}
                          >
                            {cert.name}
                          </a>
                        ) : (
                          <h3 className={`text-sm font-bold ${TEXT_BLACK_CLASS} leading-snug`}>{cert.name}</h3>
                        ))}
                      {isCertificationDatesInPreview(cert) && (
                        <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} mt-0.5`}>
                          {formatCertificationDates(cert)}
                        </div>
                      )}
                      {isCertificationIssuerInPreview(cert) && (
                        <div className={`text-sm font-normal ${TEXT_BLACK_CLASS} mt-0.5`}>
                          {cert.issuer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {interests && interests.length > 0 && (
                <div className="mb-6">
                  <SectionHeader title="INTERESTS" accentHex={colors.sectionHeaderHex} />
                  <div className={`text-sm font-normal ${TEXT_BLACK_CLASS}`}>
                    {interests.map((i, idx) => (
                      <span key={idx}>
                        {i.name}
                        {idx < interests.length - 1 && ', '}
                      </span>
                    ))}
                  </div>
                </div>
              )}
    </>
  )

  if (columnSlice === 'left') {
    return (
      <div
        className="w-full bg-white paysafe-col-left p-0"
        data-resume-template="two-column"
      >
        <div style={columnTopPadStyle}>{leftColumnContent}</div>
      </div>
    )
  }

  if (columnSlice === 'right') {
    return (
      <div
        className="w-full bg-white paysafe-col-right p-0"
        data-resume-template="two-column"
      >
        <div style={columnTopPadStyle}>{rightColumnContent}</div>
      </div>
    )
  }

  return (
    <div
      className="w-full mx-auto bg-white paysafe-two-column"
      data-resume-template="two-column"
      style={
        {
          '--resume-accent-color': colors.accentHex,
          '--resume-name-color': colors.nameHex,
          '--resume-section-color': colors.sectionHeaderHex,
        } as React.CSSProperties
      }
    >
      <div data-paysafe-header>
        <h1
          className="text-3xl font-bold mb-2 text-center"
          style={{ color: colors.nameHex }}
        >
          {fullName}
        </h1>
        {contactDetailParts.length > 0 && (
          <>
            <ContactDetailLine
              parts={contactDetailParts}
              className={`text-xs ${TEXT_BLACK_CLASS} mb-2`}
            />
            <div className="border-b border-black" />
          </>
        )}
        <span data-line-guide-header-end className="block h-0 w-full shrink-0" aria-hidden />
      </div>

      <table className="w-full border-collapse paysafe-columns" role="presentation">
        <colgroup>
          <col style={{ width: TWO_COL_PCT.left }} />
          <col style={{ width: TWO_COL_PCT.gap }} />
          <col style={{ width: TWO_COL_PCT.right }} />
        </colgroup>
        <tbody>
          <tr className="align-top">
            <td className="align-top paysafe-col-left p-0">
              <div style={columnTopPadStyle}>{leftColumnContent}</div>
            </td>
            <td className="paysafe-col-gap p-0" aria-hidden="true" />
            <td className="align-top paysafe-col-right p-0">
              <div style={columnTopPadStyle}>{rightColumnContent}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
